import React, { useState, useMemo } from 'react';
import { cleanSecurityName } from '../lib/securityName';

const fmtUSD = (v) =>
  v == null
    ? '—'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(Number(v));

const fmtNum = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { maximumFractionDigits: 4 }).format(Number(v));

// Postgres `numeric` columns arrive as strings in JSON. Sort these as numbers,
// not lexicographically, or 11149 will sort below 6402.
const NUMERIC_KEYS = new Set([
  'institution_value',
  'institution_price',
  'quantity',
  'cost_basis',
]);

function compare(a, b, key, dir) {
  const av = a[key];
  const bv = b[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (NUMERIC_KEYS.has(key)) {
    return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
  }
  if (typeof av === 'string') {
    return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }
  return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
}

const RETIREMENT_RX = /(401[(\s_-]?k|403[(\s_-]?b|\b457\b|\bira\b|\broth\b|\bhsa\b|pension|\bsep\b|\bsimple\b|retirement)/i;
const CRYPTO_RX = /(crypto|coinbase|binance|kraken|gemini|bitcoin|ethereum|wallet)/i;

function categorize(h) {
  const subtype = (h.account_subtype || '').toLowerCase();
  if (subtype.includes('401') || subtype.includes('403') || subtype.includes('ira') ||
      subtype.includes('roth') || subtype === 'retirement' || subtype === 'pension') {
    return 'retirement';
  }
  if (subtype === 'crypto' || subtype.includes('crypto')) return 'crypto';

  const blob = `${h.account_name || ''} ${h.institution_name || ''}`;
  if (RETIREMENT_RX.test(blob)) return 'retirement';
  if (CRYPTO_RX.test(blob)) return 'crypto';
  return 'brokerage';
}

const SECTION_LABEL = {
  retirement: 'Retirement',
  brokerage: 'Brokerage',
  crypto: 'Crypto',
  other: 'Other',
};

export default function HoldingsTable({ holdings, aggregateBy = 'position' }) {
  if (aggregateBy === 'sector' || aggregateBy === 'subSector') {
    return <AggregatedView holdings={holdings} groupBy={aggregateBy} />;
  }
  return <PositionView holdings={holdings} />;
}

function AggregatedView({ holdings, groupBy }) {
  const groupKey = groupBy === 'subSector' ? 'sub_sector' : 'sector';
  const groupLabel = groupBy === 'subSector' ? 'Sub-sector' : 'Sector';

  const totalPortfolioValue = useMemo(() => {
    let t = 0;
    for (const h of holdings || []) {
      const v = Number(h.institution_value);
      if (Number.isFinite(v)) t += v;
    }
    return t;
  }, [holdings]);

  const [sort, setSort] = useState({ key: 'total_value', dir: 'desc' });
  const [expanded, setExpanded] = useState(() => new Set());

  function toggleExpanded(key) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const groups = useMemo(() => {
    const map = new Map();
    for (const h of holdings || []) {
      const key = h[groupKey] || 'Unclassified';
      let g = map.get(key);
      if (!g) {
        g = { name: key, count: 0, total_value: 0, total_cost: 0, holdings: [] };
        map.set(key, g);
      }
      g.count += 1;
      g.holdings.push(h);
      const v = Number(h.institution_value);
      if (Number.isFinite(v)) g.total_value += v;
      const c = Number(h.cost_basis);
      if (Number.isFinite(c)) g.total_cost += c;
    }
    const arr = Array.from(map.values()).map((g) => ({
      ...g,
      pct: totalPortfolioValue > 0 ? (g.total_value / totalPortfolioValue) * 100 : 0,
      holdings: [...g.holdings].sort((a, b) => {
        const av = Number(a.institution_value);
        const bv = Number(b.institution_value);
        return (Number.isFinite(bv) ? bv : -Infinity) - (Number.isFinite(av) ? av : -Infinity);
      }),
    }));
    arr.sort((a, b) => {
      if (sort.key === 'name') {
        return sort.dir === 'asc'
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      const av = a[sort.key];
      const bv = b[sort.key];
      return sort.dir === 'asc' ? av - bv : bv - av;
    });
    return arr;
  }, [holdings, groupKey, sort, totalPortfolioValue]);

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  }

  if (!holdings || holdings.length === 0) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 px-4 py-6 text-center">
        <p className="text-sm text-neutral-500">
          No holdings yet. Connect an account or wait for the first sync.
        </p>
      </div>
    );
  }

  const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
  const Th = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2 ${ALIGN[align]} text-xs uppercase tracking-wide text-neutral-500 cursor-pointer select-none hover:text-neutral-300 font-normal`}
    >
      {children}
      {sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  return (
    <div className="rounded border border-neutral-800 bg-neutral-900 overflow-x-auto">
      <table className="w-full text-sm table-fixed">
        <colgroup>
          <col />
          <col className="w-24" />
          <col className="w-28" />
          <col className="w-28" />
          <col className="w-32" />
        </colgroup>
        <thead className="border-b border-neutral-800 bg-neutral-950/40">
          <tr>
            <Th k="name">{groupLabel}</Th>
            <Th k="count" align="center">Holdings</Th>
            <Th k="pct" align="center">Allocation</Th>
            <Th k="total_cost" align="center">Cost</Th>
            <Th k="total_value" align="center">Value</Th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => {
            const isExpanded = expanded.has(g.name);
            return (
              <React.Fragment key={g.name}>
                <tr
                  onClick={() => toggleExpanded(g.name)}
                  className="border-b border-neutral-900 last:border-b-0 hover:bg-neutral-900/60 cursor-pointer select-none"
                  aria-expanded={isExpanded}
                >
                  <td className="px-3 py-2 text-neutral-100 font-medium truncate">
                    <span className="inline-block w-3 text-neutral-500">
                      {isExpanded ? '▾' : '▸'}
                    </span>{' '}
                    {g.name}
                  </td>
                  <td className="px-3 py-2 text-center text-neutral-400">{g.count}</td>
                  <td className="px-3 py-2 text-center text-neutral-300">
                    {g.pct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-2 text-center text-neutral-500">
                    {g.total_cost > 0 ? fmtUSD(g.total_cost) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center text-neutral-100 font-medium">
                    {fmtUSD(g.total_value)}
                  </td>
                </tr>
                <tr aria-hidden={!isExpanded}>
                  <td colSpan={5} className="p-0 border-b border-neutral-900 last:border-b-0">
                    <div
                      className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
                      style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
                    >
                      <div className="overflow-hidden min-h-0">
                        <GroupPositions
                          holdings={g.holdings}
                          totalPortfolioValue={totalPortfolioValue}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function GroupPositions({ holdings, totalPortfolioValue }) {
  return (
    <table className="w-full text-sm bg-neutral-950/40">
      <thead className="border-y border-neutral-800">
        <tr>
          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wide text-neutral-500 font-normal w-24">
            Ticker
          </th>
          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wide text-neutral-500 font-normal">
            Name
          </th>
          <th className="px-3 py-1.5 text-left text-[10px] uppercase tracking-wide text-neutral-500 font-normal">
            Account
          </th>
          <th className="px-3 py-1.5 text-center text-[10px] uppercase tracking-wide text-neutral-500 font-normal w-28">
            Value
          </th>
          <th className="px-3 py-1.5 text-center text-[10px] uppercase tracking-wide text-neutral-500 font-normal w-20">
            Alloc
          </th>
        </tr>
      </thead>
      <tbody>
        {holdings.map((h) => {
          const v = Number(h.institution_value);
          const allocPct =
            totalPortfolioValue > 0 && Number.isFinite(v)
              ? (v / totalPortfolioValue) * 100
              : null;
          return (
            <tr
              key={h.id}
              className="border-b border-neutral-900 last:border-b-0 hover:bg-neutral-900/40"
            >
              <td className="px-3 py-1.5 text-neutral-200 font-mono truncate">
                {h.ticker || '—'}
              </td>
              <td className="px-3 py-1.5 text-neutral-300 truncate">
                {cleanSecurityName(h.security_name) || '—'}
              </td>
              <td className="px-3 py-1.5 text-neutral-500 truncate text-xs">
                {h.institution_name || '—'} · {h.account_name || '—'}
              </td>
              <td className="px-3 py-1.5 text-center text-neutral-100">
                {fmtUSD(h.institution_value)}
              </td>
              <td className="px-3 py-1.5 text-center text-neutral-400">
                {allocPct != null ? `${allocPct.toFixed(2)}%` : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PositionView({ holdings }) {
  const [sort, setSort] = useState({ key: 'institution_value', dir: 'desc' });
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [collapsedSections, setCollapsedSections] = useState(() => new Set());

  function toggleCollapsed(key) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function toggleSection(key) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const totalPortfolioValue = useMemo(() => {
    let t = 0;
    for (const h of holdings || []) {
      const v = Number(h.institution_value);
      if (Number.isFinite(v)) t += v;
    }
    return t;
  }, [holdings]);

  const sections = useMemo(() => {
    const sectionMap = new Map();
    for (const h of holdings || []) {
      const sec = categorize(h);
      let s = sectionMap.get(sec);
      if (!s) {
        s = { key: sec, label: SECTION_LABEL[sec] || 'Other', total_value: 0, accounts: new Map() };
        sectionMap.set(sec, s);
      }
      const accKey = `${h.institution_name || '—'} ${h.account_name || '—'}`;
      let a = s.accounts.get(accKey);
      if (!a) {
        a = {
          key: accKey,
          institution_name: h.institution_name || '—',
          account_name: h.account_name || '—',
          account_subtype: h.account_subtype || null,
          provider: h.provider,
          total_value: 0,
          holdings: [],
        };
        s.accounts.set(accKey, a);
      }
      a.holdings.push(h);
      const v = Number(h.institution_value);
      if (Number.isFinite(v)) {
        a.total_value += v;
        s.total_value += v;
      }
    }

    const out = Array.from(sectionMap.values()).map((s) => ({
      ...s,
      accounts: Array.from(s.accounts.values())
        .map((a) => {
          let total_cost = 0;
          for (const h of a.holdings) {
            const c = Number(h.cost_basis);
            if (Number.isFinite(c)) total_cost += c;
          }
          return {
            ...a,
            total_cost,
            holdings: [...a.holdings].sort((x, y) => compare(x, y, sort.key, sort.dir)),
          };
        })
        .sort((x, y) => y.total_value - x.total_value),
    }));
    out.sort((x, y) => y.total_value - x.total_value);
    return out;
  }, [holdings, sort]);

  function toggleSort(key) {
    setSort((s) =>
      s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' },
    );
  }

  if (!holdings || holdings.length === 0) {
    return (
      <div className="rounded border border-neutral-800 bg-neutral-900 px-4 py-6 text-center">
        <p className="text-sm text-neutral-500">
          No holdings yet. Connect an account or wait for the first sync.
        </p>
      </div>
    );
  }

  // Use literal class strings so Tailwind's content scanner picks all three
  // up. `text-${align}` doesn't get scanned, and the missing `text-left` rule
  // was letting `<th>` fall back to its HTML default of text-align: center.
  const ALIGN = { left: 'text-left', center: 'text-center', right: 'text-right' };
  const Th = ({ k, children, align = 'left' }) => (
    <th
      onClick={() => toggleSort(k)}
      className={`px-3 py-2 ${ALIGN[align]} text-xs uppercase tracking-wide text-neutral-500 cursor-pointer select-none hover:text-neutral-300 font-normal`}
    >
      {children}
      {sort.key === k ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );

  // Shared column layout — must be identical between the holdings table and
  // the always-visible totals table for the columns to line up across them.
  const ColGroup = () => (
    <colgroup>
      <col className="w-24" />
      <col />
      <col className="w-32" />
      <col className="w-24" />
      <col className="w-24" />
      <col className="w-28" />
      <col className="w-20" />
      <col className="w-24" />
    </colgroup>
  );

  return (
    <div className="space-y-6">
      {sections.map((s) => {
        const sectionCollapsed = collapsedSections.has(s.key);
        const sectionAllocPct =
          totalPortfolioValue > 0 ? (s.total_value / totalPortfolioValue) * 100 : 0;
        return (
        <section
          key={s.key}
          className="rounded-lg border border-neutral-800 bg-neutral-900/30 overflow-hidden"
        >
          <header className="flex items-center justify-between px-4 py-3 border-b border-neutral-800 bg-neutral-900/60">
            <div className="flex items-center gap-3 min-w-0">
              <h3 className="text-base font-semibold text-neutral-100">{s.label}</h3>
              <span className="text-xs text-neutral-500">
                {s.accounts.length} account{s.accounts.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              {totalPortfolioValue > 0 && (
                <span className="text-xs text-neutral-400 tabular-nums">
                  {sectionAllocPct.toFixed(2)}%
                </span>
              )}
              <span className="text-sm font-semibold text-neutral-100 tabular-nums">
                {fmtUSD(s.total_value)}
              </span>
              <button
                type="button"
                onClick={() => toggleSection(s.key)}
                aria-expanded={!sectionCollapsed}
                aria-label={sectionCollapsed ? 'Expand section' : 'Collapse section'}
                className="px-2 py-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800 text-sm"
              >
                {sectionCollapsed ? '▸' : '▾'}
              </button>
            </div>
          </header>

          <div
            className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
            style={{ gridTemplateRows: sectionCollapsed ? '0fr' : '1fr' }}
            aria-hidden={sectionCollapsed}
          >
            <div className="overflow-hidden min-h-0">
              <div className="p-4 space-y-4">
            {s.accounts.map((a) => {
              const isCollapsed = collapsed.has(a.key);
              const allocPct =
                totalPortfolioValue > 0
                  ? (a.total_value / totalPortfolioValue) * 100
                  : 0;
              return (
              <div
                key={a.key}
                className="rounded border border-neutral-800 bg-neutral-900 overflow-x-auto"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs uppercase tracking-wide text-neutral-500">
                      {a.institution_name}
                    </span>
                    <span className="text-neutral-700">·</span>
                    <span className="text-sm font-medium text-neutral-200 truncate">
                      {a.account_name}
                    </span>
                    <span className="text-xs text-neutral-600 ml-1 shrink-0">
                      {a.holdings.length} holding{a.holdings.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleCollapsed(a.key)}
                    aria-expanded={!isCollapsed}
                    aria-label={isCollapsed ? 'Expand account' : 'Collapse account'}
                    className="shrink-0 ml-3 px-2 py-1 rounded text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800 text-sm"
                  >
                    {isCollapsed ? '▸' : '▾'}
                  </button>
                </div>

                {/* Animated collapsible holdings region. Uses the
                    grid-template-rows 0fr↔1fr trick for smooth height
                    transitions without measuring content. */}
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none"
                  style={{ gridTemplateRows: isCollapsed ? '0fr' : '1fr' }}
                  aria-hidden={isCollapsed}
                >
                  <div className="overflow-hidden min-h-0">
                <table className="w-full text-sm table-fixed">
                  <ColGroup />
                  <thead className="border-b border-neutral-800 bg-neutral-950/40">
                    <tr>
                      <Th k="ticker">Ticker</Th>
                      <Th k="security_name">Name</Th>
                      <Th k="sector">Sector</Th>
                      <Th k="quantity" align="center">Qty</Th>
                      <Th k="institution_price" align="center">Price</Th>
                      <Th k="institution_value" align="center">Value</Th>
                      <Th k="institution_value" align="center">Alloc</Th>
                      <Th k="cost_basis" align="center">Cost</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {a.holdings.map((h) => (
                      <tr
                        key={h.id}
                        className="border-b border-neutral-900 last:border-b-0 hover:bg-neutral-900/60"
                      >
                        <td className="px-3 py-2 text-neutral-100 font-mono truncate">
                          {h.ticker || '—'}
                        </td>
                        <td className="px-3 py-2 text-neutral-300 truncate">
                          {cleanSecurityName(h.security_name) || '—'}
                        </td>
                        <td className="px-3 py-2 text-neutral-400 truncate">
                          {h.sector || '—'}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-300">
                          {fmtNum(h.quantity)}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-300">
                          {fmtUSD(h.institution_price)}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-100 font-medium">
                          {fmtUSD(h.institution_value)}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-300">
                          {totalPortfolioValue > 0 && Number.isFinite(Number(h.institution_value))
                            ? `${((Number(h.institution_value) / totalPortfolioValue) * 100).toFixed(2)}%`
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-center text-neutral-500">
                          {fmtUSD(h.cost_basis)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                </div>

                {/* Totals — always visible. Lives in its own table sharing
                    the same colgroup so columns line up exactly. */}
                <table className="w-full text-sm table-fixed border-t border-neutral-800 bg-neutral-950/40">
                  <ColGroup />
                  <tbody>
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-2 text-right text-xs uppercase tracking-wide text-neutral-500"
                      >
                        Total
                      </td>
                      <td className="px-3 py-2 text-center text-neutral-100 font-semibold">
                        {fmtUSD(a.total_value)}
                      </td>
                      <td className="px-3 py-2 text-center text-neutral-300 font-medium">
                        {totalPortfolioValue > 0 ? `${allocPct.toFixed(2)}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-center text-neutral-400">
                        {a.total_cost > 0 ? fmtUSD(a.total_cost) : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              );
            })}
              </div>
            </div>
          </div>
        </section>
        );
      })}
    </div>
  );
}
